import { getFromDataPath, reduceDataPath } from "../../../utility";
import {
  ARST_INSERT_COLUMN_AFTER,
  ARST_INSERT_COLUMN_BEFORE,
  ARST_INSERT_OPERATION_COLUMN_AFTER,
  ARST_INSERT_OPERATION_COLUMN_BEFORE,
  ARST_INSERT_OPERATION_ROW_AFTER,
  ARST_INSERT_OPERATION_SUBGROUP,
  ARST_INSERT_ROW_AFTER,
  ARST_INSERT_SUBGROUP,
  ArsTableState,
  ArstActionDropAction,
} from "../interface";
import { isArsTableGroupCalculationDto } from "../typeguards";
import { arst_resolveTargetPath } from "./arst_resolveTargetPath";
import { insertItemAt } from "./insertItemAt";

/**
 * Resolves the insertion position for dropped ARS items and delegates the state update.
 *
 * @param state - Current ARS table state
 * @param action - Drop action describing the target row, column or subgroup position
 * @returns Updated ARS table state, or the original state when the target is invalid
 */
export const arst_insertDroppedItem = <T>(
  state: ArsTableState<T>,
  action: ArstActionDropAction,
): ArsTableState<T> => {
  const targetCell = state.scaffoldModel?.cellData?.[action.rowId]?.[action.columnId];
  if (!targetCell) return state;

  const targetDataPath = arst_resolveTargetPath(targetCell.dataPath);
  // Get path parts here
  const targetPathParts = targetDataPath.split("/").filter(Boolean);
  // Resolve if column or row
  const isInsertColumn = [
    ARST_INSERT_COLUMN_AFTER,
    ARST_INSERT_COLUMN_BEFORE,
    ARST_INSERT_OPERATION_COLUMN_AFTER,
    ARST_INSERT_OPERATION_COLUMN_BEFORE,
  ].includes(action.operation);
  // Inserting at start of root
  let groupsPath = isInsertColumn ? "/columns" : "/rows";
  let insertIndex = 0;

  // Insert subGroup under target group
  if ([ARST_INSERT_SUBGROUP, ARST_INSERT_OPERATION_SUBGROUP].includes(action.operation)) {
    // Get target group
    const currentGroup = getFromDataPath(targetDataPath, state.bodyData, (obj) =>
      isArsTableGroupCalculationDto(obj, state.settings.editorProps.isT),
    );
    // Abort if target is not found
    if (!currentGroup) return state;

    // Get new subGroups for the current group
    groupsPath = `${targetDataPath}/subGroups`;
    insertIndex = currentGroup.subGroups?.length ?? 0;
  }
  // All remaining ArstDropOperation variants insert a sibling rather than a subgroup.
  else {
    // Adding at a lower level within the hierarchy
    if (targetPathParts.length > 0 && targetPathParts[0] !== "rowHeaders") {
      const thisIndex = parseInt(targetPathParts[targetPathParts.length - 1], 10);
      if (Number.isNaN(thisIndex)) return state;
      // Resolve if before or after
      const isInsertAfter = [
        ARST_INSERT_COLUMN_AFTER,
        ARST_INSERT_ROW_AFTER,
        ARST_INSERT_OPERATION_COLUMN_AFTER,
        ARST_INSERT_OPERATION_ROW_AFTER,
      ].includes(action.operation);
      // Update inserting index if inserting after
      insertIndex = thisIndex + (isInsertAfter ? 1 : 0);

      // Update table groups, either rows or columns
      const parentPath = reduceDataPath(targetDataPath, 2);
      const parentGroup = getFromDataPath(parentPath, state.bodyData, (obj) =>
        isArsTableGroupCalculationDto(obj, state.settings.editorProps.isT),
      );
      if (parentGroup) {
        groupsPath = `${parentPath}/subGroups`;
      }
      // Inserting at root level uses the same position as the header drop path.
    }
  }

  return insertItemAt(state, action.payload, groupsPath, insertIndex);
};
